import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Wallet,
  RefreshCcw,
  PackageX,
  Archive,
  PackageCheck,
  ShieldAlert,
  ArrowRight,
} from 'lucide-react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import { ScoreTile } from './charts/ScoreTile';
import { ChartCard, EmptyChart } from './charts/ChartPrimitives';
import { OverviewTabSkeleton } from './ReportingTabSkeletons';
import {
  formatUnits,
  formatRate,
  formatDaysOfCover,
  deltaBand,
  inventoryTurnsBand,
  daysOfCoverBand,
  buyingActionBand,
  RAG_TEXT,
  RAG_PILL,
  type BuyingAction,
} from '../lib/reporting-format';
import { buyingActionLabel } from './on-order/types';

type ReorderSignal = {
  skuId: string;
  sku: string;
  category: string;
  productType: string;
  onHand: number;
  notifyThreshold: number | null;
  runRate: number;
  daysOfCover: number | null;
  belowThreshold: boolean;
  reasons: string[];
  // Inbound cross-reference (added by the On-Order work). Optional so this card
  // degrades gracefully if an older payload lacks them.
  buyingAction?: BuyingAction;
  incoming?: number;
  nextEtaDate?: string | null;
};

export interface OverviewSummary {
  totalStockValue: number;
  stockValue30dAgo: number;
  stockValueDelta: number;
  basis: 'WAC';
  deadStockValue: number;
  inventoryTurns: number | null;
  blanksBelowReorder: number;
  worstBlank: string | null;
  ordersFulfilled: number;
  throughputSparkline: Array<{ date: string; units: number }>;
  exceptions: ReorderSignal[];
}

/** Short word for the turns band, shown as the tile's coloured pill. */
function turnsBandLabel(band: ReturnType<typeof inventoryTurnsBand>, t: TFunction): string {
  switch (band) {
    case 'good':
      return t('reporting.v2.turnsStrong', 'Strong');
    case 'attention':
      return t('reporting.v2.turnsSlow', 'Slow');
    default:
      return t('reporting.v2.turnsSteady', 'Steady');
  }
}

interface OverviewTabProps {
  loading: boolean;
  data: OverviewSummary | undefined;
  /** Jump to a deep tab (Performance / Stock Value). */
  onNavigate: (tab: 'performance' | 'costs') => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ loading, data, onNavigate }) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  if (loading && !data) return <OverviewTabSkeleton />;
  if (!data) {
    return (
      <EmptyChart
        message={t('reporting.v2.noOverview', 'No overview data for this period yet.')}
      />
    );
  }

  const blanksBand = data.blanksBelowReorder > 0 ? 'attention' : 'good';

  return (
    <div className="space-y-6">
      {/* Scorecards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <ScoreTile
          label={t('reporting.v2.tile.stockValue', 'Total stock value')}
          value={format(data.totalStockValue, currency)}
          icon={Wallet}
          delta={
            data.stockValueDelta !== 0
              ? `${data.stockValueDelta > 0 ? '+' : ''}${format(
                  data.stockValueDelta,
                  currency,
                )}`
              : null
          }
          deltaBand={deltaBand(data.stockValueDelta)}
          detail={t('reporting.v2.vs30d', 'vs 30 days ago, WAC')}
          onActivate={() => onNavigate('costs')}
          activateLabel={t('reporting.v2.tile.stockValueGo', 'View stock value breakdown')}
        />

        <ScoreTile
          label={t('reporting.v2.tile.turns', 'Inventory turns')}
          value={
            data.inventoryTurns == null
              ? '-'
              : t('reporting.v2.turnsUnit', '{{n}}x', { n: formatRate(data.inventoryTurns) })
          }
          icon={RefreshCcw}
          bandLabel={
            data.inventoryTurns == null
              ? undefined
              : turnsBandLabel(inventoryTurnsBand(data.inventoryTurns), t)
          }
          band={inventoryTurnsBand(data.inventoryTurns)}
          detail={t('reporting.v2.annualised', 'Annualised, COGS over avg value')}
          onActivate={() => onNavigate('performance')}
          activateLabel={t('reporting.v2.tile.turnsGo', 'View performance')}
        />

        <ScoreTile
          label={t('reporting.v2.tile.blanks', 'Blanks below reorder')}
          value={formatUnits(data.blanksBelowReorder)}
          icon={PackageX}
          band={blanksBand}
          bandLabel={
            data.blanksBelowReorder > 0
              ? t('reporting.v2.podRisk', 'POD risk')
              : t('reporting.v2.allClear', 'Clear')
          }
          detail={
            data.worstBlank
              ? t('reporting.v2.worst', 'Worst: {{sku}}', { sku: data.worstBlank })
              : t('reporting.v2.noBlankRisk', 'No blanks under threshold')
          }
          onActivate={() => onNavigate('performance')}
          activateLabel={t('reporting.v2.tile.blanksGo', 'View reorder signals')}
        />

        <ScoreTile
          label={t('reporting.v2.tile.deadStock', 'Dead stock (90+ days)')}
          value={format(data.deadStockValue, currency)}
          icon={Archive}
          band={data.deadStockValue > 0 ? 'attention' : 'good'}
          detail={t('reporting.v2.atRisk', 'Value idle 90+ days')}
          onActivate={() => onNavigate('costs')}
          activateLabel={t('reporting.v2.tile.deadStockGo', 'View aging breakdown')}
        />

        <ScoreTile
          label={t('reporting.v2.tile.fulfilled', 'Orders fulfilled')}
          value={formatUnits(data.ordersFulfilled)}
          icon={PackageCheck}
          sparkline={data.throughputSparkline}
          detail={t('reporting.v2.thisPeriod', 'This period')}
          onActivate={() => onNavigate('performance')}
          activateLabel={t('reporting.v2.tile.fulfilledGo', 'View throughput')}
        />

        <ScoreTile
          label={t('reporting.v2.tile.attention', 'Needs attention')}
          value={formatUnits(data.exceptions.length)}
          icon={ShieldAlert}
          band={data.exceptions.length > 0 ? 'attention' : 'good'}
          detail={t('reporting.v2.reorderSignals', 'Open reorder signals')}
          onActivate={() => onNavigate('performance')}
          activateLabel={t('reporting.v2.tile.attentionGo', 'View reorder signals')}
        />
      </div>

      {/* Needs-attention strip */}
      <ChartCard
        title={t('reporting.v2.attentionStrip', 'Needs attention')}
        description={t(
          'reporting.v2.attentionStripDesc',
          'Top reorder signals across POD blanks and finished goods.',
        )}
        action={
          data.exceptions.length > 0 ? (
            <button
              type="button"
              onClick={() => onNavigate('performance')}
              className="inline-flex items-center gap-1 text-xs font-medium text-threadheads-purple hover:underline"
            >
              {t('reporting.v2.viewAll', 'View all')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : undefined
        }
      >
        {data.exceptions.length === 0 ? (
          <EmptyChart
            message={t(
              'reporting.v2.noExceptions',
              'Nothing needs reordering right now. Every tracked SKU is above its threshold.',
            )}
            className="min-h-[8rem]"
          />
        ) : (
          <ul className="divide-y">
            {data.exceptions.slice(0, 5).map((signal) => {
              const band = daysOfCoverBand(signal.daysOfCover);
              return (
                <li
                  key={`${signal.skuId}-${signal.sku}`}
                  className="flex items-center justify-between gap-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm font-medium">{signal.sku}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {signal.category}
                      {' · '}
                      {signal.productType}
                      {signal.reasons.length > 0 && ` · ${signal.reasons.join(', ')}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4 text-right">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('reporting.v2.onHand', 'On hand')}
                      </p>
                      <p className="text-sm font-semibold tabular-nums">
                        {formatUnits(signal.onHand)}
                        {signal.notifyThreshold != null && (
                          <span className="font-normal text-muted-foreground">
                            {' / '}
                            {formatUnits(signal.notifyThreshold)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="w-16">
                      <p className="text-xs text-muted-foreground">
                        {t('reporting.v2.cover', 'Cover')}
                      </p>
                      <p
                        className={cn(
                          'text-sm font-semibold tabular-nums',
                          RAG_TEXT[band],
                        )}
                      >
                        {formatDaysOfCover(signal.daysOfCover)}
                      </p>
                    </div>
                    {signal.buyingAction && (
                      <span
                        className={cn(
                          'inline-flex w-28 justify-center whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold',
                          RAG_PILL[buyingActionBand(signal.buyingAction)],
                        )}
                      >
                        {buyingActionLabel(signal.buyingAction, t)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ChartCard>
    </div>
  );
};
